import { forwardRef, HTMLAttributes, memo, useContext, useEffect, useState } from "react";
import { differenceInHours, differenceInMinutes } from "date-fns";
import { DateUtilsContext } from "../../../core";
import { Tooltip, TooltipProps } from "../../Tooltip";
import { DateTimeRenderer } from "../DateTimeRenderer";
import { BaseDateTimeRendererProps } from "../types";
import { getShortTime } from "./utils";

export type RelativeTimeRendererProps = HTMLAttributes<HTMLSpanElement> & BaseDateTimeRendererProps & {
  tooltipProps?: Omit<TooltipProps, "content" | "children">
};

export const RelativeTimeRenderer = memo(forwardRef<HTMLSpanElement, RelativeTimeRendererProps>((
  {
    value,
    timeZone,
    dateFormat,
    tooltipProps,
    ...props
  },
  ref,
) => {
  const { convertFromServerTime } = useContext(DateUtilsContext);
  const [ now, setNow ] = useState(new Date());

  useEffect(() => {
    if (value) {
      let timeout;
      const eventDate = value instanceof Date ? value : convertFromServerTime(value)!;
      if (differenceInMinutes(now, eventDate) < 60) {
        // every 30 seconds +/- 5s
        timeout = setTimeout(() => setNow(new Date()), 25 * 1000 + (Math.random() * 10 * 1000));
      } else if (differenceInHours(now, eventDate) < 24) {
        // every 15 minutes +/- 5m
        timeout = setTimeout(() => setNow(new Date()), 15 * 60 * 1000 + (Math.random() * 10 * 60 * 1000));
      }
      return () => clearTimeout(timeout!);
    }

  }, [ value, convertFromServerTime, now ]);

  const eventDate = (!value || value instanceof Date) ? value as Date : convertFromServerTime(value);
  if (!eventDate) {
    return null;
  }

  const showRelative = differenceInHours(now, eventDate) >= 0 && differenceInHours(now, eventDate) < 24;

  return (
    <Tooltip
      content={
        showRelative
          ? <DateTimeRenderer value={value} dateFormat={dateFormat} timeZone={timeZone} />
          : ""
      }
      {...tooltipProps}
    >
      <span {...props} ref={ref}>
        {
          showRelative
            ? getShortTime(eventDate, now)
            : <DateTimeRenderer
              value={value}
              dateFormat={dateFormat}
              timeZone={timeZone}
            />
        }
      </span>
    </Tooltip>
  );
}));
